import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router";

import { usePollTokenExpiration } from "@vega-api/custom/hooks/usePollTokenExpiration";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useInactivityAutoLogout } from "@vega-core/hooks/useInactivityAutoLogout";
import { isF2F, isMSW } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { useTasksGuard } from "../../context/TasksStateContext/useTasksGuard";
import { CommonPaths } from "../../paths";
import { usePendingTasks } from "../PendingTasks/usePendingTasks";
import { useTxIdCapture } from "../PendingTasks/useTxIdCapture";

import { ErrorBoundary } from "./ErrorBoundary";

export const GPPrivateRoute = ({ children }: PropsWithChildren) => {
  usePollTokenExpiration({ logoutPath: CommonPaths.Logout });
  useInactivityAutoLogout({ logoutPath: CommonPaths.Logout });
  useTxIdCapture(FrontendPortal.GP_PORTAL);
  usePendingTasks();

  const session = useSession();
  const location = useLocation();

  const { shouldRedirect, shouldShowLoader: isLoadingTasks } = useTasksGuard();

  const { featureFlag } = useFeatureFlag();
  const isV2TasksEnabled = Boolean(featureFlag[FeatureFlag.LP_AUTH_LOGIN_TASK]);

  const isMockEnvironment = isMSW || isF2F;

  if (isFalsy(session.user)) {
    return <Navigate replace to={CommonPaths.Login} />;
  }

  if (isV2TasksEnabled && isLoadingTasks) {
    return <LoadingSpinner />;
  }

  if (isV2TasksEnabled && shouldRedirect && !isMockEnvironment) {
    return (
      <Navigate replace state={{ from: location }} to={CommonPaths.Welcome} />
    );
  }

  return (
    <ErrorBoundary isPublicPage={false} locationKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
};
